import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const UserProfiles = () => {
  const [activeProfile, setActiveProfile] = useState('it');

  // Animate elements on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    // Observe stat cards and use case cards
    const cards = document.querySelectorAll('.stat-card, .use-case-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeProfile]);

  const showProfile = (profileId) => {
    setActiveProfile(profileId);
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Profile Selector */
        .profile-selector {
            display: flex;
            justify-content: center;
            gap: 2rem;
            margin-bottom: 4rem;
            flex-wrap: wrap;
        }
        
        .profile-tab {
            padding: 1rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s;
            text-align: center;
        }
        
        .profile-tab:hover {
            background: rgba(255, 255, 255, 0.08);
            transform: translateY(-2px);
        }
        
        .profile-tab.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: transparent;
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .profile-icon {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        
        .profile-name {
            font-weight: 600;
            font-size: 1.1rem;
        }
        
        /* Profile Content */
        .profile-content {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .profile-content.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* Profile Overview */
        .profile-overview {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin-bottom: 3rem;
            position: relative;
            overflow: hidden;
        }
        
        .profile-overview::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 100%;
            height: 100%;
            background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
            opacity: 0.5;
        }
        
        .overview-header {
            display: flex;
            align-items: center;
            gap: 2rem;
            margin-bottom: 2rem;
            position: relative;
            z-index: 1;
        }
        
        .overview-icon {
            width: 100px;
            height: 100px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 3rem;
        }
        
        .overview-info h2 {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        
        .overview-description {
            color: #bbb;
            line-height: 1.6;
            position: relative;
            z-index: 1;
        }
        
        /* Stats Grid */
        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin: 2rem 0;
        }
        
        .stat-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            text-align: center;
            transition: all 0.3s;
        }
        
        .stat-card:hover {
            transform: scale(1.05);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .stat-value {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
            color: #888;
            font-size: 0.9rem;
            margin-top: 0.5rem;
        }
        
        /* Use Cases Section */
        .use-cases-section {
            margin: 3rem 0;
        }
        
        .section-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 2rem;
        }
        
        .section-icon {
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .use-case-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 1.5rem;
        }
        
        .use-case-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .use-case-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
            background: rgba(255, 255, 255, 0.05);
        }
        
        .use-case-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #fff;
        }
        
        .use-case-query {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1rem;
            font-family: monospace;
            font-size: 0.9rem;
            color: #c792ea;
        }
        
        .use-case-response {
            color: #bbb;
            font-size: 0.95rem;
            line-height: 1.5;
        }
        
        .use-case-tags {
            display: flex;
            gap: 0.5rem;
            margin-top: 1rem;
            flex-wrap: wrap;
        }
        
        .tag {
            padding: 0.3rem 0.8rem;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            font-size: 0.8rem;
            color: #888;
        }
        
        /* Access Rights Table */
        .access-table {
            width: 100%;
            border-collapse: collapse;
            margin: 2rem 0;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            overflow: hidden;
        }
        
        .access-table th,
        .access-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .access-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: 600;
        }
        
        .access-table tr:last-child td {
            border-bottom: none;
        }
        
        .access-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .access-check {
            color: #67e8a9;
            font-size: 1.2rem;
        }
        
        .access-cross {
            color: #f56565;
            font-size: 1.2rem;
        }
        
        .access-partial {
            color: #ed8936;
            font-size: 1.2rem;
        }
        
        /* Comparison Section */
        .comparison-section {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin: 3rem 0;
        }
        
        .comparison-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 2rem;
            margin-top: 2rem;
        }
        
        .comparison-card {
            text-align: center;
            padding: 2rem 1rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 12px;
            transition: all 0.3s;
        }
        
        .comparison-card:hover {
            transform: scale(1.05);
            background: rgba(102, 126, 234, 0.1);
        }
        
        .comparison-icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
        }
        
        .comparison-title {
            font-weight: bold;
            margin-bottom: 1rem;
        }
        
        .comparison-features {
            list-style: none;
            color: #888;
            font-size: 0.9rem;
        }
        
        .comparison-features li {
            padding: 0.3rem 0;
        }
        
        /* Tips Section */
        .tips-box {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
            border-radius: 12px;
            padding: 1.5rem;
            margin: 2rem 0;
        }
        
        .tips-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
        }
        
        .tips-icon {
            color: #ed8936;
            font-size: 1.5rem;
        }
        
        .tips-list {
            list-style: none;
            padding-left: 2rem;
        }
        
        .tips-list li {
            padding: 0.5rem 0;
            position: relative;
        }
        
        .tips-list li::before {
            content: '💡';
            position: absolute;
            left: -1.5rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .profile-selector {
                flex-direction: column;
                align-items: stretch;
            }
            
            .overview-header {
                flex-direction: column;
                text-align: center;
            }
            
            .use-case-grid {
                grid-template-columns: 1fr;
            }
            
            .comparison-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Profils Utilisateurs</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Profils Utilisateurs</h1>
          <p className="page-subtitle">Réponses personnalisées selon votre rôle dans l'organisation</p>
        </div>
        
        {/* Profile Selector */}
        <div className="profile-selector">
          <div 
            className={`profile-tab ${activeProfile === 'it' ? 'active' : ''}`} 
            onClick={() => showProfile('it')}
          >
            <div className="profile-icon">💻</div>
            <div className="profile-name">IT</div>
          </div>
          <div 
            className={`profile-tab ${activeProfile === 'backoffice' ? 'active' : ''}`} 
            onClick={() => showProfile('backoffice')}
          >
            <div className="profile-icon">🏦</div>
            <div className="profile-name">BackOffice</div>
          </div>
          <div 
            className={`profile-tab ${activeProfile === 'amoa' ? 'active' : ''}`} 
            onClick={() => showProfile('amoa')}
          >
            <div className="profile-icon">📊</div>
            <div className="profile-name">AMOA</div>
          </div>
          <div 
            className={`profile-tab ${activeProfile === 'moe' ? 'active' : ''}`} 
            onClick={() => showProfile('moe')}
          >
            <div className="profile-icon">🏗️</div>
            <div className="profile-name">MOE</div>
          </div>
        </div>
        
        {/* IT Profile */}
        <div className={`profile-content ${activeProfile === 'it' ? 'active' : ''}`}>
          <div className="profile-overview">
            <div className="overview-header">
              <div className="overview-icon">💻</div>
              <div className="overview-info">
                <h2>Profil IT - Support Technique</h2>
                <p style={{ color: '#888' }}>Développeurs, Administrateurs Système, Support L2/L3</p>
              </div>
            </div>
            <p className="overview-description">
              Le profil IT donne accès aux informations techniques détaillées : code source, configurations, 
              logs d'erreur, architecture système. Les réponses incluent des exemples de code, commandes 
              spécifiques et solutions de debugging avancées.
            </p>
          </div>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">100%</div>
              <div className="stat-label">Accès Code Source</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">Full</div>
              <div className="stat-label">Détails Techniques</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">Expert</div>
              <div className="stat-label">Niveau Complexité</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">24/7</div>
              <div className="stat-label">Support Prioritaire</div>
            </div>
          </div>
          
          <div className="use-cases-section">
            <div className="section-header">
              <div className="section-icon">🎯</div>
              <h3>Cas d'Usage Typiques</h3>
            </div>
            
            <div className="use-case-grid">
              <div className="use-case-card">
                <h4 className="use-case-title">Debugging Erreur Oracle</h4>
                <div className="use-case-query">
                  "Erreur ORA-12154 dans le module de paiement lors de la connexion à PAYDB"
                </div>
                <p className="use-case-response">
                  Analyse détaillée avec vérification du tnsnames.ora, test de connectivité réseau, 
                  exemples de code 4GL pour la gestion d'erreur...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Database</span>
                  <span className="tag">Debugging</span>
                  <span className="tag">Code</span>
                </div>
              </div>
              
              <div className="use-case-card">
                <h4 className="use-case-title">Configuration Batch</h4>
                <div className="use-case-query">
                  "Comment optimiser les paramètres du batch de clôture nocturne?"
                </div>
                <p className="use-case-response">
                  Paramètres de tuning recommandés, configuration des threads, 
                  gestion mémoire, monitoring des performances...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Performance</span>
                  <span className="tag">Configuration</span>
                  <span className="tag">Batch</span>
                </div>
              </div>
              
              <div className="use-case-card">
                <h4 className="use-case-title">Architecture API</h4>
                <div className="use-case-query">
                  "Architecture pour intégrer le nouveau service SWIFT MT103"
                </div>
                <p className="use-case-response">
                  Diagrammes techniques, endpoints API, format des messages, 
                  sécurité et authentification, code d'exemple...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Integration</span>
                  <span className="tag">API</span>
                  <span className="tag">Architecture</span>
                </div>
              </div>
            </div>
          </div>
          
          <table className="access-table">
            <thead>
              <tr>
                <th>Type de Contenu</th>
                <th>Niveau d'Accès</th>
                <th>Détails</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Code Source 4GL</td>
                <td><span className="access-check">✓</span> Complet</td>
                <td>Accès à tous les modules avec explications détaillées</td>
              </tr>
              <tr>
                <td>Configurations Système</td>
                <td><span className="access-check">✓</span> Complet</td>
                <td>Paramètres serveur, DB, réseau, sécurité</td>
              </tr>
              <tr>
                <td>Logs & Monitoring</td>
                <td><span className="access-check">✓</span> Complet</td>
                <td>Analyse des logs, métriques performance</td>
              </tr>
              <tr>
                <td>Architecture Technique</td>
                <td><span className="access-check">✓</span> Complet</td>
                <td>Diagrammes détaillés, flux techniques</td>
              </tr>
              <tr>
                <td>Procédures Métier</td>
                <td><span className="access-partial">◐</span> Vue Technique</td>
                <td>Focus sur l'implémentation technique</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* BackOffice Profile */}
        <div className={`profile-content ${activeProfile === 'backoffice' ? 'active' : ''}`}>
          <div className="profile-overview">
            <div className="overview-header">
              <div className="overview-icon">🏦</div>
              <div className="overview-info">
                <h2>Profil BackOffice - Opérations</h2>
                <p style={{ color: '#888' }}>Gestionnaires, Opérateurs, Support Client</p>
              </div>
            </div>
            <p className="overview-description">
              Le profil BackOffice accède aux procédures métier, guides utilisateurs et réglementations. 
              Les réponses sont orientées processus avec des étapes claires, sans détails techniques complexes. 
              Focus sur l'efficacité opérationnelle et la conformité.
            </p>
          </div>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">Step-by-Step</div>
              <div className="stat-label">Format Réponses</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">100%</div>
              <div className="stat-label">Procédures</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">Simplifié</div>
              <div className="stat-label">Langage</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">Pratique</div>
              <div className="stat-label">Orientation</div>
            </div>
          </div>
          
          <div className="use-cases-section">
            <div className="section-header">
              <div className="section-icon">🎯</div>
              <h3>Cas d'Usage Typiques</h3>
            </div>
            
            <div className="use-case-grid">
              <div className="use-case-card">
                <h4 className="use-case-title">Traitement Chèque Impayé</h4>
                <div className="use-case-query">
                  "Comment traiter un chèque impayé avec opposition client?"
                </div>
                <p className="use-case-response">
                  1. Vérifier le motif d'impayé dans le système<br/>
                  2. Appliquer les frais selon barème<br/>
                  3. Notifier le client sous 48h...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Procédure</span>
                  <span className="tag">Compliance</span>
                  <span className="tag">Client</span>
                </div>
              </div>
              
              <div className="use-case-card">
                <h4 className="use-case-title">Ouverture Compte</h4>
                <div className="use-case-query">
                  "Procédure complète ouverture compte entreprise OHADA"
                </div>
                <p className="use-case-response">
                  Documents requis, vérifications KYC, étapes de validation, 
                  délais réglementaires, cas particuliers...
                </p>
                <div className="use-case-tags">
                  <span className="tag">KYC</span>
                  <span className="tag">Ouverture</span>
                  <span className="tag">OHADA</span>
                </div>
              </div>
              
              <div className="use-case-card">
                <h4 className="use-case-title">Virement International</h4>
                <div className="use-case-query">
                  "Limites et procédures virements SWIFT zone CEMAC"
                </div>
                <p className="use-case-response">
                  Plafonds applicables, documents justificatifs, 
                  délais de traitement, commissions...
                </p>
                <div className="use-case-tags">
                  <span className="tag">SWIFT</span>
                  <span className="tag">International</span>
                  <span className="tag">Limites</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* AMOA Profile */}
        <div className={`profile-content ${activeProfile === 'amoa' ? 'active' : ''}`}>
          <div className="profile-overview">
            <div className="overview-header">
              <div className="overview-icon">📊</div>
              <div className="overview-info">
                <h2>Profil AMOA - Analyse Fonctionnelle</h2>
                <p style={{ color: '#888' }}>Business Analysts, Product Owners, Chefs de Projet</p>
              </div>
            </div>
            <p className="overview-description">
              Le profil AMOA combine vision métier et compréhension technique. Accès aux spécifications 
              fonctionnelles, analyses d'impact, réglementations et leurs implications sur les processus. 
              Réponses orientées analyse et recommandations stratégiques.
            </p>
          </div>
          
          <div className="use-cases-section">
            <div className="section-header">
              <div className="section-icon">🎯</div>
              <h3>Cas d'Usage Typiques</h3>
            </div>
            
            <div className="use-case-grid">
              <div className="use-case-card">
                <h4 className="use-case-title">Analyse d'Impact</h4>
                <div className="use-case-query">
                  "Impact nouvelle directive BEAC 2024 sur nos processus"
                </div>
                <p className="use-case-response">
                  Analyse détaillée des changements requis, modules impactés, 
                  estimation effort, plan de mise en conformité...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Impact</span>
                  <span className="tag">Réglementation</span>
                  <span className="tag">Planning</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* MOE Profile */}
        <div className={`profile-content ${activeProfile === 'moe' ? 'active' : ''}`}>
          <div className="profile-overview">
            <div className="overview-header">
              <div className="overview-icon">🏗️</div>
              <div className="overview-info">
                <h2>Profil MOE - Architecture & Développement</h2>
                <p style={{ color: '#888' }}>Architectes, Tech Leads, DevOps</p>
              </div>
            </div>
            <p className="overview-description">
              Le profil MOE accède à l'architecture système complète, patterns de conception, 
              best practices et documentation technique avancée. Réponses incluant considérations 
              de scalabilité, sécurité et performance.
            </p>
          </div>
          
          <div className="use-cases-section">
            <div className="section-header">
              <div className="section-icon">🎯</div>
              <h3>Cas d'Usage Typiques</h3>
            </div>
            
            <div className="use-case-grid">
              <div className="use-case-card">
                <h4 className="use-case-title">Migration Architecture</h4>
                <div className="use-case-query">
                  "Stratégie migration modules 4GL vers microservices"
                </div>
                <p className="use-case-response">
                  Architecture cible, découpage en domaines, stratégie de migration 
                  progressive, gestion des données, patterns recommandés...
                </p>
                <div className="use-case-tags">
                  <span className="tag">Architecture</span>
                  <span className="tag">Migration</span>
                  <span className="tag">Microservices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Comparison Section */}
        <div className="comparison-section">
          <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>Comparaison des Profils</h2>
          <div className="comparison-grid">
            <div className="comparison-card">
              <div className="comparison-icon">💻</div>
              <h4 className="comparison-title">IT</h4>
              <ul className="comparison-features">
                <li>Code source complet</li>
                <li>Détails techniques</li>
                <li>Debugging avancé</li>
                <li>Architecture système</li>
              </ul>
            </div>
            <div className="comparison-card">
              <div className="comparison-icon">🏦</div>
              <h4 className="comparison-title">BackOffice</h4>
              <ul className="comparison-features">
                <li>Procédures métier</li>
                <li>Guides pratiques</li>
                <li>Réglementation</li>
                <li>Support client</li>
              </ul>
            </div>
            <div className="comparison-card">
              <div className="comparison-icon">📊</div>
              <h4 className="comparison-title">AMOA</h4>
              <ul className="comparison-features">
                <li>Analyse fonctionnelle</li>
                <li>Impact business</li>
                <li>Spécifications</li>
                <li>Roadmap produit</li>
              </ul>
            </div>
            <div className="comparison-card">
              <div className="comparison-icon">🏗️</div>
              <h4 className="comparison-title">MOE</h4>
              <ul className="comparison-features">
                <li>Architecture globale</li>
                <li>Patterns & best practices</li>
                <li>Scalabilité</li>
                <li>DevOps & CI/CD</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Tips */}
        <div className="tips-box">
          <div className="tips-header">
            <span className="tips-icon">💡</span>
            <h3>Conseils pour Optimiser vos Requêtes</h3>
          </div>
          <ul className="tips-list">
            <li>Précisez toujours votre contexte et objectif</li>
            <li>Utilisez la terminologie spécifique à votre domaine</li>
            <li>Mentionnez les contraintes (délais, budget, réglementation)</li>
            <li>Indiquez si vous cherchez une solution rapide ou optimale</li>
            <li>N'hésitez pas à demander des exemples concrets</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default UserProfiles;